/**
 * Site-wide constants for SEO: canonical origin, sitemap generation and the
 * JSON-LD publisher identity.
 *
 * The site answers on BOTH `balivernologie.info` (apex) and
 * `www.balivernologie.info`. `www` is the CANONICAL hostname: every canonical
 * link, `og:url`, sitemap `<loc>` and JSON-LD `url` this app emits must use it
 * whatever host the visitor actually typed, or the two hostnames compete as
 * duplicate content and split their ranking signals. The apex is 301'd to
 * `www` at the edge (see `vercel.json` and the deploy workflows); this
 * constant is the in-app half of the same rule, and the reason nothing here
 * derives an origin from `window.location`.
 *
 * `VITE_SITE_URL` still overrides the default, so a fork or a staging origin
 * can build a self-consistent sitemap without patching source.
 */

import { localizedPath } from '@/application/i18n/locale-path'
import type { SupportedLangCode } from '@/application/i18n/language'

/** Preferred (canonical) hostname — the one every emitted URL must carry. */
export const CANONICAL_HOST = 'www.balivernologie.info'

/** Non-canonical variant, redirected to {@link CANONICAL_HOST} at the edge. */
export const APEX_HOST = 'balivernologie.info'

/** Canonical origin used whenever `VITE_SITE_URL` is not set. */
export const DEFAULT_SITE_URL = `https://${CANONICAL_HOST}`

function normalizeOrigin(rawUrl: string): string {
  return rawUrl.trim().replace(/\/+$/, '')
}

const configuredSiteUrl =
  typeof import.meta !== 'undefined' ? import.meta.env['VITE_SITE_URL'] : undefined

export const SITE_URL = normalizeOrigin(configuredSiteUrl || DEFAULT_SITE_URL)

/**
 * Path of the default social-preview image, relative to the site root.
 *
 * Single source of truth on purpose: `index.html`, every page's `og:image`,
 * the backend's crawler pre-render and the JSON-LD logo all resolve through
 * it. It points at a purpose-built 1200x630 `summary_large_image` card
 * (`frontend/public/og-card.jpg`, generated by
 * `frontend/scripts/generate-og-card.sh`) rather than the square logo, which
 * Twitter/X and LinkedIn would letterbox or crop to a circle.
 */
export const OG_IMAGE_PATH = '/og-card.jpg'

/** Intrinsic size and MIME type of {@link OG_IMAGE_PATH}, for the `og:image:*` tags. */
export const OG_IMAGE_WIDTH = 1200
export const OG_IMAGE_HEIGHT = 630
export const OG_IMAGE_TYPE = 'image/jpeg'

/** Absolute URL of the default social-preview image. */
export const OG_IMAGE_URL = `${SITE_URL}${OG_IMAGE_PATH}`

/**
 * `og:locale` values per supported language.
 *
 * Open Graph wants a full `language_TERRITORY` locale, not the bare ISO-639-1
 * code the app routes on, and a page must publish the one it is actually
 * rendered in plus the alternates it links to via hreflang.
 */
export const OG_LOCALES: Record<SupportedLangCode, string> = {
  fr: 'fr_FR',
  en: 'en_US',
  es: 'es_ES',
}

/**
 * Builds an absolute, canonical-host URL from a root-relative path.
 *
 * The site root keeps its trailing slash (`https://host/`) so the home page's
 * canonical, `og:url` and sitemap `<loc>` are byte-identical — a canonical
 * that disagrees with the sitemap on a trailing slash is two URLs to Google.
 */
export function absoluteUrl(path: string): string {
  if (!path || path === '/') return `${SITE_URL}/`
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`
}

/**
 * Absolute URL of a language-free path AS SERVED IN A GIVEN LANGUAGE.
 *
 * `('/about', 'fr')` -> `https://host/about`
 * `('/about', 'en')` -> `https://host/en/about`
 *
 * Every canonical, `og:url`, hreflang alternate, sitemap `<loc>` and JSON-LD
 * `url` goes through this, so a page can never canonicalise across locales.
 */
export function localizedAbsoluteUrl(path: string, locale: SupportedLangCode): string {
  return absoluteUrl(localizedPath(path, locale))
}

/**
 * Resolves an investigation cover into something a crawler can actually fetch.
 *
 * Uploaded covers are served by the BACKEND, on a different origin, and are
 * already absolute in production (`BACKEND_URL`). A value that is not absolute
 * has no origin a script-less crawler could resolve it against — it would be
 * read relative to the crawler's own base and 404 — so the site's own preview
 * image is published instead of an unresolvable `og:image`.
 */
export function absoluteImageUrl(imageUrl: string | null | undefined): string {
  if (imageUrl && /^https?:\/\//i.test(imageUrl)) return imageUrl
  return OG_IMAGE_URL
}

/** Publisher identity mirrored from the legal pages (Part 2) for JSON-LD. */
export const PUBLISHER_NAME = 'Bertrand Presles'

/** Stable JSON-LD node ids, so the graph's nodes can reference each other. */
export const WEBSITE_LD_ID = `${SITE_URL}/#website`
export const PUBLISHER_LD_ID = `${SITE_URL}/#publisher`
